/**
 * screens/settings/billing.tsx — Billing & Invoices (P-007), ADMIN-only.
 * Faithful RN port of design/plan.jsx BillingPage + RazorpayModal: failed-payment
 * alert, payment method card, upcoming CGST/SGST breakdown, invoice history with
 * expandable tax rows, retry + Razorpay redirect. Wired to platformApi.
 */

import React, { useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { useResponsive } from '../../utils/responsive';
import { SettingsHeader, Panel, Divider, StatusBadge } from '../../components/platform/primitives';
import { PButton, PModal } from '../../components/platform/controls';
import Alert from '../../components/alert';
import Icon from '../../components/icon';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW, R, inr } from '../../utils/platformTokens';
import { addPaymentMethod } from '../../services/platform/platformApi';

const Row: React.FC<{ label: string; value: string; bold?: boolean; mono?: boolean }> = ({ label, value, bold, mono }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
      <Text style={{ color: bold ? c.ink : c.ink3, fontWeight: bold ? FW.semibold : FW.regular, fontSize: FS.md }}>{label}</Text>
      <Text style={{ color: c.ink, fontWeight: bold ? FW.semibold : FW.regular, fontSize: FS.md, fontFamily: mono ? theme.font.mono : undefined }}>{value}</Text>
    </View>
  );
};

const RazorpayModal: React.FC<{ open: boolean; onClose: () => void; orgId: string }> = ({ open, onClose, orgId }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();
  const [stage, setStage] = useState<'boundary' | 'redirecting' | 'done'>('boundary');
  React.useEffect(() => { if (open) setStage('boundary'); }, [open]);

  const go = async () => {
    setStage('redirecting');
    await addPaymentMethod(orgId).catch(() => undefined);
    setTimeout(() => { setStage('done'); toast.success('Payment method updated.'); }, 1400);
  };

  return (
    <PModal
      open={open}
      onClose={onClose}
      size="sm"
      title="Update billing"
      closeOnBackdrop={stage !== 'redirecting'}
      footer={
        stage === 'boundary' ? (
          <>
            <PButton kind="ghost" onPress={onClose}>Cancel</PButton>
            <PButton kind="accent" icon="external" onPress={go}>Continue to Razorpay</PButton>
          </>
        ) : stage === 'done' ? <PButton kind="accent" onPress={onClose}>Done</PButton> : undefined
      }
    >
      {stage === 'boundary' && (
        <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-start' }}>
          <View style={{ width: 40, height: 40, borderRadius: R.r2, backgroundColor: c.accentSoft, alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="lock" size={18} color={c.accent} />
          </View>
          <Text style={{ flex: 1, color: c.ink2 }}>
            You'll be taken to Razorpay's secure portal to update your card. Softknack never stores your card details. You'll return here when you're done.
          </Text>
        </View>
      )}
      {stage === 'redirecting' && (
        <View style={{ alignItems: 'center', paddingVertical: 24 }}>
          <ActivityIndicator size="large" color={c.accent} />
          <Text style={{ marginTop: 14, color: c.ink2 }}>Redirecting to Razorpay…</Text>
        </View>
      )}
      {stage === 'done' && <Alert tone="success" title="All set">Your billing method was updated and synced back to your workspace.</Alert>}
    </PModal>
  );
};

const BillingScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, role } = usePlatform();
  const { width } = useResponsive();
  const toast = useToast();
  const [rzp, setRzp] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  if (role !== 'ADMIN') return <PermissionDenied />;
  const hasFailed = state.invoices.some((i) => i.status === 'failed');
  const twoCol = width > 720;

  const retry = (id: string) => { setRetrying(id); setTimeout(() => { setRetrying(null); toast.success('Payment retried successfully.'); }, 1000); };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1000, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Billing" sub="Payment method, upcoming charges and tax invoices." />

      {hasFailed && (
        <View style={{ marginBottom: 20 }}>
          <Alert tone="danger" title="A payment failed">
            Invoice INV-2026-0019 couldn't be charged. Update your method or retry to avoid interruption.
          </Alert>
        </View>
      )}

      <View style={{ flexDirection: twoCol ? 'row' : 'column', gap: 16 }}>
        <View style={{ flex: 1 }}>
          <Panel title="Payment method" style={{ marginBottom: 0 }}
            actions={<PButton size="sm" icon="external" onPress={() => setRzp(true)}>Update billing</PButton>}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <View style={{ width: 44, height: 30, borderRadius: 4, backgroundColor: c.ink, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ color: c.paper, fontSize: 10, fontWeight: '700', letterSpacing: 1 }}>VISA</Text>
              </View>
              <View>
                <Text style={{ fontFamily: theme.font.mono, fontWeight: FW.medium, color: c.ink }}>•••• •••• •••• 4242</Text>
                <Text style={{ fontSize: FS.sm, color: c.ink3 }}>Expires 08/27 · via Razorpay</Text>
              </View>
            </View>
          </Panel>
        </View>
        <View style={{ flex: 1 }}>
          <Panel title="Upcoming charge" sub="Estimated · 01 Jul 2026" style={{ marginBottom: 0 }}>
            <View style={{ gap: 6 }}>
              <Row label="Standard plan" value={inr(1270.34)} mono />
              <Row label="CGST (9%)" value={inr(114.33)} mono />
              <Row label="SGST (9%)" value={inr(114.33)} mono />
              <Divider style={{ marginVertical: 6 }} />
              <Row label="Total" value={inr(1499)} bold mono />
            </View>
          </Panel>
        </View>
      </View>

      <View style={{ height: 16 }} />

      <Panel title="Invoice history" style={{ marginBottom: 0 }} bodyStyle={{ padding: 0 }}>
        {state.invoices.map((inv, idx) => (
          <View key={inv.id} style={{ borderTopWidth: idx ? 1 : 0, borderColor: c.line }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 12 }}>
              <Text style={{ flex: 1.4, fontFamily: theme.font.mono, color: c.ink, fontSize: FS.md }}>{inv.id}</Text>
              <Text style={{ flex: 1, color: c.ink2, fontSize: FS.md }}>{inv.date}</Text>
              <Text style={{ flex: 0.8, fontFamily: theme.font.mono, color: c.ink, fontSize: FS.md }}>{inr(inv.amount)}</Text>
              <View style={{ flex: 0.8 }}><StatusBadge status={inv.status} /></View>
              <View style={{ flexDirection: 'row', gap: 6, justifyContent: 'flex-end', flex: 1.4 }}>
                {inv.status === 'failed' && <PButton size="sm" kind="accent" loading={retrying === inv.id} onPress={() => retry(inv.id)}>Retry</PButton>}
                <PButton size="sm" kind="ghost" onPress={() => setExpanded(expanded === inv.id ? null : inv.id)}>{expanded === inv.id ? 'Hide tax' : 'Tax'}</PButton>
                <PButton size="sm" kind="ghost" icon="download" onPress={() => toast.success('Invoice PDF downloaded.')}>PDF</PButton>
              </View>
            </View>
            {expanded === inv.id && (
              <View style={{ backgroundColor: c.paper2, paddingHorizontal: 16, paddingVertical: 10, flexDirection: 'row', flexWrap: 'wrap', gap: 24 }}>
                <Text style={{ color: c.ink3, fontSize: FS.sm }}>Taxable value <Text style={{ color: c.ink, fontFamily: theme.font.mono }}>{inr(inv.base)}</Text></Text>
                <Text style={{ color: c.ink3, fontSize: FS.sm }}>CGST 9% <Text style={{ color: c.ink, fontFamily: theme.font.mono }}>{inr(+(inv.base * 0.09).toFixed(2))}</Text></Text>
                <Text style={{ color: c.ink3, fontSize: FS.sm }}>SGST 9% <Text style={{ color: c.ink, fontFamily: theme.font.mono }}>{inr(+(inv.base * 0.09).toFixed(2))}</Text></Text>
                <Text style={{ color: c.ink3, fontSize: FS.sm }}>Total <Text style={{ color: c.ink, fontFamily: theme.font.mono }}>{inr(inv.amount)}</Text></Text>
              </View>
            )}
          </View>
        ))}
      </Panel>

      <RazorpayModal open={rzp} onClose={() => setRzp(false)} orgId={state.org.name} />
    </ScrollView>
  );
};

export default BillingScreen;
